import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import { cn } from '@/lib/cn';

interface HintProps {
  /** Dotted key into the `help` i18n namespace — e.g. `errorType.M`. */
  k: string;
  /** Override the icon size class (defaults to `h-3.5 w-3.5`). */
  className?: string;
  /** Right-aligned by default; pass `start` to flip when next to a label. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Max width of the tooltip text in px. */
  maxWidth?: number;
}

/**
 * Inline question-mark icon that surfaces a tooltip with localised help text.
 *
 * Usage:
 *   <Hint k="errorType.M" />            // beside a label or column header
 *   <Hint k="pipelineState.idle" side="right" maxWidth={320} />
 *
 * All copy lives in `locales/{lang}/help.json`. Keep it concise — long
 * explanations belong in the (forthcoming) help center pages.
 */
export function Hint({ k, className, side = 'top', maxWidth = 280 }: HintProps) {
  const { t } = useTranslation('help');
  const text = t(k, { defaultValue: '' });
  if (!text) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex cursor-help align-middle text-slate-400 hover:text-slate-600"
          tabIndex={0}
          aria-label={text}
        >
          <HelpCircle className={cn('h-3.5 w-3.5', className)} aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} style={{ maxWidth }}>
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
